/* ============================================================
   Rennoa — Lazy third-party loader
   One place for all deferred / facade-loaded apps.

   ASSUMES these app embeds are turned OFF in Theme Editor
   (otherwise they'll double-load):
     - Gorgias
     - Klaviyo Onsite Javascript
     - Adtraction App Extension
     - Contribe Cart
   (Contribe Badge is handled separately by snippets/contribe-badge-lazy.liquid)
   (Microsoft Clarity should be fully disabled — not loaded here)

   HOW TO INCLUDE — add this near the end of <body> in layout/theme.liquid:
     <script src="{{ 'custom.js' | asset_url }}" defer></script>
   ============================================================ */

(() => {
  'use strict';

  // ---- Config: vendor loaders / IDs -------------------------
  const CONFIG = {
    klaviyo:    'https://static.klaviyo.com/onsite/js/Tfz7ds/klaviyo.js?company_id=Tfz7ds',
    gorgias:    'https://config.gorgias.chat/bundle-loader/shopify/rennoa.myshopify.com',
    adtraction: 'https://static.adtrcdn.io/adtraction-shopify.js?shop=rennoa.myshopify.com',
    // Contribe is intentionally NOT loaded here — see section 4 below.
    // It is handled by its native app embeds (Contribe Badge + Contribe Cart).
    interactionTimeoutMs: 4000, // fallback for Klaviyo if no interaction
  };

  // ---- Helpers ----------------------------------------------
  const loaded = {}; // guard against double-loading

  function injectScript(src, { module = false } = {}) {
    if (loaded[src]) return loaded[src];
    loaded[src] = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      if (module) s.type = 'module';
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return loaded[src];
  }

  function onIdle(cb) {
    if ('requestIdleCallback' in window) requestIdleCallback(cb, { timeout: 3000 });
    else setTimeout(cb, 1500);
  }

  function afterLoad(cb) {
    if (document.readyState === 'complete') cb();
    else window.addEventListener('load', cb, { once: true });
  }

  /* --------------------------------------------------------------
     CONSENT GATE (GDPR / ePrivacy)
     Marketing + analytics scripts should fire only AFTER consent.
     This tries to read CookieScript; if it can't determine state,
     it falls back to "allowed" so functionality isn't silently broken.
     >>> Verify this matches your CookieScript category names. <<<
  -------------------------------------------------------------- */
  function marketingConsentGranted() {
    try {
      if (window.CookieScript && window.CookieScript.instance) {
        const state = window.CookieScript.instance.currentState(); // { categories: [...] }
        if (state && Array.isArray(state.categories)) {
          return state.categories.includes('targeting') ||
                 state.categories.includes('performance') ||
                 state.categories.includes('marketing');
        }
      }
    } catch (e) { /* ignore */ }
    return true; // fallback — tighten this for strict compliance
  }

  // Run cb now if consent is granted, otherwise wait for CookieScript accept
  function whenMarketingConsent(cb) {
    if (marketingConsentGranted()) { cb(); return; }
    document.addEventListener('cookiescriptaccept', cb, { once: true });
  }

  // ---- 1) KLAVIYO — load on first interaction OR timeout ----
  (function klaviyo() {
    let done = false;
    const go = () => {
      if (done) return; done = true;
      cleanup();
      whenMarketingConsent(() => injectScript(CONFIG.klaviyo));
    };
    const events = ['scroll', 'pointerdown', 'keydown', 'touchstart', 'mousemove'];
    const cleanup = () => events.forEach(e => window.removeEventListener(e, go));
    events.forEach(e => window.addEventListener(e, go, { once: true, passive: true }));
    setTimeout(go, CONFIG.interactionTimeoutMs); // fallback so it still loads for bouncers
  })();

  // ---- 2) ADTRACTION — load on idle after page load ---------
  //  Affiliate attribution: don't gate on interaction (could miss
  //  fast bounces), but it IS marketing -> respect consent.
  (function adtraction() {
    afterLoad(() => onIdle(() => {
      whenMarketingConsent(() => injectScript(CONFIG.adtraction));
    }));
  })();

  // ---- 3) GORGIAS — click-to-load chat facade ---------------
  (function gorgias() {
    // Build a fake chat bubble
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'gorgias-facade';
    btn.setAttribute('aria-label', 'Åbn chat');
    btn.innerHTML =
      '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
      '<path d="M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H8l-4 4V5a1 1 0 0 1 1-1z" ' +
      'fill="currentColor"/></svg>';

    btn.addEventListener('click', () => {
      btn.disabled = true;
      // Gorgias chat is a functional widget the user explicitly opened.
      injectScript(CONFIG.gorgias).then(() => {
        // Poll for Gorgias API, then open the real widget
        let tries = 0;
        const open = setInterval(() => {
          tries++;
          if (window.GorgiasChat && typeof window.GorgiasChat.open === 'function') {
            window.GorgiasChat.open();
            btn.remove(); // remove facade; real widget takes over
            clearInterval(open);
          } else if (tries > 40) { // ~10s safety stop
            clearInterval(open);
            btn.disabled = false;
          }
        }, 250);
      }).catch(() => { btn.disabled = false; });
    }, { once: false });

    afterLoad(() => document.body.appendChild(btn));
  })();

  /* ---- 4) CONTRIBE (badge + cart) — handled by the app embeds -
     We tried self-loading Contribe (facade badge + injecting a
     <contribe-plugin> into the Kaching cart on open), but Contribe's
     own code expects to manage placement, the dynamically-rendered
     cart, and cart context (cartId/cartSize/cartValue) itself, so the
     hand-rolled version was unreliable.

     DECISION: leave Contribe to its native app embeds.
       - Re-enable "Contribe Badge" and "Contribe Cart" in
         Theme Editor → App embeds.
       - Remove {% render 'contribe-badge-lazy' %} from buy-buttons.liquid.
     Contribe is only ~40 KB, so this is a small cost vs. the big wins
     (Gorgias, Klaviyo, Adtraction, Intelligems, GoWish) above.
  ------------------------------------------------------------- */

  /* ---- 5) JUDGE.ME — NOT loaded here -------------------------
     Recommendation: keep the Judge.me app embed ENABLED and turn on
     its built-in "lazy load reviews widget" setting. The embed renders
     your above-the-fold star rating, and its own lazy option defers the
     heavy reviews list. Self-loading Judge.me from a versioned extension
     URL is fragile (the URL changes on every app update) and would risk
     breaking the top star rating, so it's intentionally left to the app.
  ------------------------------------------------------------- */

})();

/* ============================================================
   Facade styles (or move these into custom.css)
   ============================================================ */
(() => {
  const css = `
    .gorgias-facade{
      position:fixed; right:20px; bottom:20px; z-index:9998;
      width:56px; height:56px; border:none; border-radius:50%;
      background:#274c77; color:#fff; cursor:pointer;
      display:flex; align-items:center; justify-content:center;
      box-shadow:0 4px 14px rgba(0,0,0,.25);
      transition:transform .15s ease, opacity .15s ease;
    }
    .gorgias-facade:hover{ transform:scale(1.06); }
    .gorgias-facade:disabled{ opacity:.6; cursor:default; }
    @media (max-width:749px){ .gorgias-facade{ right:14px; bottom:14px; } }
  `;
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
})();